/**
 * Utility module: getMatchStats/getCardsAndGoals.js.
 */

import { getAwayGoals, getHomeGoals } from './getGoals';
import { getAwayRedCards, getHomeRedCards } from './getCards';

/** @typedef {import('./types').Match} Match */
/** @typedef {import('./types').Goal} Goal */
/** @typedef {import('./types').Card} Card */

/**
 * Combine goals + red cards for a side, sorted chronologically by `timeMin`.
 *
 * @param {Match} match
 * @param {'home' | 'away'} side
 * @returns {(Goal | Card)[]}
 */
const getGoalsAndRedCardsForSide = (match, side) => {
  const goals = side === 'home' ? getHomeGoals(match) : getAwayGoals(match);
  const cards =
    side === 'home' ? getHomeRedCards(match) : getAwayRedCards(match);
  return [...goals, ...cards].sort(
    (a, b) => (a.timeMin ?? 0) - (b.timeMin ?? 0)
  );
};

/**
 * Home team's goals and red cards, sorted by match minute. Useful when
 * rendering a single in-order timeline of significant events for the side.
 *
 * @param {Match} match
 * @returns {(Goal | Card)[]}
 */
export const getHomeGoalsAndRedCards = (match) =>
  getGoalsAndRedCardsForSide(match, 'home');

/**
 * Away team's goals and red cards, sorted by match minute.
 *
 * @param {Match} match
 * @returns {(Goal | Card)[]}
 */
export const getAwayGoalsAndRedCards = (match) =>
  getGoalsAndRedCardsForSide(match, 'away');
